import React, { useMemo } from 'react';
import { ChevronLeft, Timer, Plus, Info } from 'lucide-react';
import { Button, Badge, Card, Tag, Tooltip } from 'antd';
import { useTheme } from '../../../contexts/ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';
import GoalsTable from './GoalsTable';
import SoftSkillsSection from './SoftSkillsSection';
import ReviewSummary from './ReviewSummary';
import { GoalsTableSkeleton, SoftSkillsSkeleton, ReviewSummarySkeleton } from './GoalsSkeleton';
import GoalsStatChips from './GoalsStatChips';
import GoalsValidationStrip from './GoalsValidationStrip';

const GoalsDetailView = ({
  selectedMonth,
  selectedYear,
  bannerConfig,
  goals,
  softSkillsData,
  reviewSummary,
  isReviewSummaryExpanded,
  onToggleReviewSummary,
  onReviewChange,
  onBack,
  onAddGoal,
  onApproveGoal,
  onRejectGoal,
  onDeleteGoal,
  onEditGoal,
  onSetAchievedPercent,
  onSoftSkillRatingChange,
  canEditEmployeeRating = false,
  canEditManagerRating = false,
  canEditEmployeeResponse = true,
  canEditManagerResponse = true,
  achievements = [],
  onAddAchievement,
  isGoalsCycleEditable,
  isSubmissionWindowActive,
  isReviewWindowActive,
  isEvaluationWindowActive = false,
  isViewedMonthCycleEvaluated = false,
  isViewingTeamMember,
  onBadgeInfoClick,
  onStreakInfoClick,
  inlineNewGoal,
  setInlineNewGoal,
  editingGoalId,
  setEditingGoalId,
  badgeRanges = [],
  onSaveNewGoal,
  onSaveEditGoal,
  onCancelEdit,
  minGoals = 0,
  maxGoals = 0,
  criticalRequired = 0,
  minWeightage = 10,
  maxWeightage = 100,
  isManager = false,
  currentUserId = null,
  targetUserId = null,
  cycleId = null,
  onCalculateScore = null,
  loading = false,
  softSkillsLoading = false,
  reviewSummaryLoading = false,
  currentCycle = null,
  streakIconUrl = null,
  employeeAvgScore = null,
  onSubmitGoals = null,
  scoreRanges = [],
  isOrgAdmin = false,
  isAutonomousMode = false,
  onToggleCritical,
  softSkillsEnabled = true,
  aiGoalEnabled = false,
  onToggleAiGoal = null,
  projectsList = [],
  onSelfEvaluate = null,
  onVerifyGoal = null,
}) => {
  const { mode } = useTheme();
  const isDark = mode === 'dark';

  // Find badge icon from settings based on rankLabel
  const getBadgeIcon = () => {
    const badgeLabel = bannerConfig?.rankLabel || '';
    if (!badgeRanges || badgeRanges.length === 0) return null;

    // Try exact match first (case insensitive)
    let badgeRange = badgeRanges.find(range =>
      range.label.toLowerCase() === badgeLabel.toLowerCase()
    );

    // If no exact match, try partial match
    if (!badgeRange && badgeLabel) {
      badgeRange = badgeRanges.find(range =>
        badgeLabel.toLowerCase().includes(range.label.toLowerCase()) ||
        range.label.toLowerCase().includes(badgeLabel.toLowerCase())
      );
    }

    // If still no match, try to find first badge with icon (fallback)
    if (!badgeRange) {
      badgeRange = badgeRanges.find(range => range.icon?.url && range.icon.url.trim() !== '');
    }

    const iconUrl = badgeRange?.icon?.url;
    return (iconUrl && typeof iconUrl === 'string' && iconUrl.trim() !== '') ? iconUrl : null;
  };

  const badgeIconUrl = getBadgeIcon();

  // Calculate weighted score for completed months (same logic as backend)
  // Use goal.score if available (from backend calculation), otherwise calculate from achievedPercent
  const getScoreFromAchieved = (achievedPercent) => {
    if (achievedPercent == null || isNaN(achievedPercent)) return 0;
    const p = Math.max(0, Math.min(100, achievedPercent));

    // Try to use scoreRanges from settings
    if (scoreRanges && scoreRanges.length > 0) {
      const range = scoreRanges.find(r => p >= r.min && p <= r.max);
      if (range) return range.score;
    }

    // Simple mapping: 0-10%→1, 11-20%→2, ..., 91-100%→10
    return Math.min(10, Math.max(1, Math.floor(p / 10) + (p % 10 > 0 ? 1 : 0)));
  };

  // Calculate weighted average score (same as backend logic)
  const calculateWeightedScore = () => {
    if (!goals || goals.length === 0) return 0;

    // Filter only approved goals with scores
    const approvedGoals = goals.filter(g => g.status === 'APPROVED');
    if (approvedGoals.length === 0) return 0;

    // Get goals with scores (either from goal.score or calculate from achievedPercent)
    const goalsWithScores = approvedGoals.map(goal => {
      let goalScore = null;

      // Prefer goal.score if available (from backend calculation)
      if (goal.score != null && goal.score !== undefined && !isNaN(goal.score)) {
        goalScore = parseFloat(goal.score);
      } else if (goal.achievedPercent != null && goal.achievedPercent !== undefined) {
        // Fallback: calculate from achievedPercent
        goalScore = getScoreFromAchieved(goal.achievedPercent || goal.achieved || 0);
      }

      return {
        score: goalScore,
        weightage: goal.weightage || goal.weight || 0
      };
    }).filter(g => g.score != null && g.score !== undefined && g.weightage > 0);

    if (goalsWithScores.length === 0) return 0;

    // Calculate weighted average
    let totalWeightedScore = 0;
    let totalWeight = 0;

    goalsWithScores.forEach(goal => {
      totalWeightedScore += goal.score * goal.weightage;
      totalWeight += goal.weightage;
    });

    if (totalWeight === 0) return 0;

    const finalScore = totalWeightedScore / totalWeight;
    return Math.floor(finalScore * 10) / 10; // Truncate to 1 decimal place
  };

  const monthScore = calculateWeightedScore();

  // Format submission end date for display
  const formatSubmissionEndDate = () => {
    if (!currentCycle?.submissionEnd) {
      return '30 MAR 2026'; // Fallback date
    }

    const submissionEndDate = new Date(currentCycle.submissionEnd);
    const day = submissionEndDate.getDate();
    const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
    const month = monthNames[submissionEndDate.getMonth()];
    const year = submissionEndDate.getFullYear();

    return `${day} ${month} ${year}`;
  };

  const submissionEndDateText = formatSubmissionEndDate();



  // Check if cycle is truly over (all windows closed)
  // Don't rely on isGoalsCycleEditable - check cycle status and windows directly
  // Default to "NOT over" if we can't determine - be defensive
  const isCycleTrulyOver = useMemo(() => {
    // Org Admin in autonomous mode can ALWAYS edit THEIR OWN goals, so cycle is NEVER "truly over" for them
    if (isOrgAdmin && isAutonomousMode && !isViewingTeamMember) return false;

    if (!currentCycle) return false; // If no cycle data, don't show "CYCLE OVER"

    // If cycle is locked, it's explicitly locked by an admin — always treat as over
    if (currentCycle.status === 'locked') return true;

    // CHECK DATES FIRST (primary source of truth)
    // cycle.status may be stale (e.g. 'completed') if settings changed dates without resetting status
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    // Check submission window
    if (currentCycle.submissionStart && currentCycle.submissionEnd) {
      const submissionStart = new Date(currentCycle.submissionStart);
      const submissionEnd = new Date(currentCycle.submissionEnd);
      submissionStart.setHours(0, 0, 0, 0);
      submissionEnd.setHours(23, 59, 59, 999);

      if (now >= submissionStart && now <= submissionEnd) return false;
    }

    // Check review window
    if (currentCycle.reviewStart && currentCycle.reviewEnd) {
      const reviewStart = new Date(currentCycle.reviewStart);
      const reviewEnd = new Date(currentCycle.reviewEnd);
      reviewStart.setHours(0, 0, 0, 0);
      reviewEnd.setHours(23, 59, 59, 999);

      if (now >= reviewStart && now <= reviewEnd) return false;
    }

    // Check evaluation window
    if (currentCycle.evaluationStart && currentCycle.evaluationEnd) {
      const evaluationStart = new Date(currentCycle.evaluationStart);
      const evaluationEnd = new Date(currentCycle.evaluationEnd);
      evaluationStart.setHours(0, 0, 0, 0);
      evaluationEnd.setHours(23, 59, 59, 999);

      if (now >= evaluationStart && now <= evaluationEnd) return false;
    }

    // SECONDARY: If no date windows are active, NOW check status
    // If status is 'submission', 'review', or 'evaluation' but dates don't match,
    // still trust the status as a fallback
    if (currentCycle.status === 'submission' || currentCycle.status === 'review' || currentCycle.status === 'evaluation') {
      return false;
    }

    // If we reach here, no date windows are active AND status is not actively 'submission'/'review'/'evaluation'
    if (!currentCycle.status || currentCycle.status.trim() === '') return false;

    return true;
  }, [currentCycle, isOrgAdmin, isAutonomousMode, isViewingTeamMember]);

  // Check if user has a valid score (greater than 0)
  const hasValidScore = monthScore != null && monthScore !== undefined && parseFloat(monthScore) > 0;

  // Check if streak is valid
  const hasValidStreak = bannerConfig?.currentStreak &&
    bannerConfig.currentStreak !== '0 Month' &&
    bannerConfig.currentStreak !== '0 Months' &&
    bannerConfig.currentStreak !== '0' &&
    !bannerConfig.currentStreak.includes('0 Month');

  return (
    <div className="space-y-4 pb-24 text-left" style={{ fontFamily: '-apple-system, system-ui, "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, sans-serif' }}>
      {/* Header row — back button + month title + window status */}
      <motion.div
        className="flex items-center justify-between"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, type: "spring", stiffness: 120 }}
      >
        <div className="flex items-center gap-3">
          <motion.button
            onClick={onBack}
            className={`w-8 h-8 rounded-lg border flex items-center justify-center flex-shrink-0 ${isDark
              ? 'bg-white/5 border-white/10 text-white/60 hover:text-blue-400 hover:border-blue-500/30'
              : 'bg-white border-slate-200 text-slate-500 hover:text-blue-600 hover:border-blue-300'
              }`}
            whileTap={{ scale: 0.95 }}
          >
            <ChevronLeft size={16} strokeWidth={2.5} />
          </motion.button>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className={`text-[15px] font-semibold leading-tight ${isDark ? '!text-white' : 'text-slate-900'}`}>
              Goals — {selectedMonth} {selectedYear}
            </h1>
            {isSubmissionWindowActive ? (
              <div className={`flex items-center gap-1 px-1.5 py-px rounded ${isDark ? 'bg-amber-500/15 text-amber-400' : 'bg-amber-50 text-amber-700'}`}>
                <Timer size={10} />
                <span className="text-[11px] font-medium">
                  Submit by {submissionEndDateText}
                </span>
              </div>
            ) : isCycleTrulyOver ? (
              <div className={`flex items-center gap-1 px-1.5 py-px rounded ${isDark ? 'bg-red-500/15 text-red-400' : 'bg-red-50 text-red-600'}`}>
                <Info size={10} />
                <span className="text-[11px] font-medium">Cycle over</span>
              </div>
            ) : null}
          </div>
        </div>
      </motion.div>

      {/* Status / Streak / Goals Added / Score — same chip row as the dashboard */}
      <GoalsStatChips
        bannerConfig={bannerConfig}
        badgeIconUrl={badgeIconUrl}
        streakIconUrl={streakIconUrl}
        employeeAvgScore={monthScore}
        goalsCount={(goals || []).length}
        maxGoals={maxGoals}
        onBadgeInfoClick={onBadgeInfoClick}
        onStreakInfoClick={onStreakInfoClick}
      />

      {!loading && isSubmissionWindowActive && !isViewingTeamMember && (
        <GoalsValidationStrip
          goals={goals || []}
          minGoals={minGoals}
          maxGoals={maxGoals}
          criticalRequired={criticalRequired}
          className="mb-4"
        />
      )}

      <AnimatePresence mode="wait">
        {loading ? (
          <motion.div
            key="goals-skeleton"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <GoalsTableSkeleton />
          </motion.div>
        ) : (
          <motion.div
            key="goals-table"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <GoalsTable
              goals={goals || []}
              onApprove={onApproveGoal}
              onReject={onRejectGoal}
              onDelete={onDeleteGoal}
              onEdit={onEditGoal}
              onSetAchievedPercent={onSetAchievedPercent}
              onCalculateScore={onCalculateScore}
              canAddGoal={isSubmissionWindowActive || isReviewWindowActive}
              onAddGoal={onAddGoal}
              isGoalsCycleEditable={isGoalsCycleEditable}
              inlineNewGoal={inlineNewGoal}
              setInlineNewGoal={setInlineNewGoal}
              editingGoalId={editingGoalId}
              setEditingGoalId={setEditingGoalId}
              onSaveNewGoal={onSaveNewGoal}
              onSaveEditGoal={onSaveEditGoal}
              onCancelEdit={onCancelEdit}
              currentUserId={currentUserId}
              targetUserId={targetUserId}
              cycleId={cycleId}
              minGoals={minGoals}
              maxGoals={maxGoals}
              criticalRequired={criticalRequired}
              minWeightage={minWeightage}
              maxWeightage={maxWeightage}
              isManager={isManager}
              isViewingTeamMember={isViewingTeamMember}
              isSubmissionWindowActive={isSubmissionWindowActive}
              isReviewWindowActive={isReviewWindowActive}
              isEvaluationWindowActive={isEvaluationWindowActive}
              isOrgAdmin={isOrgAdmin}
              isAutonomousMode={isAutonomousMode}
              currentCycle={currentCycle}
              onSubmitGoals={onSubmitGoals}
              scoreRanges={scoreRanges}
              onToggleCritical={onToggleCritical}
              aiGoalEnabled={aiGoalEnabled}
              onToggleAiGoal={onToggleAiGoal}
              projectsList={projectsList}
              onSelfEvaluate={onSelfEvaluate}
              onVerifyGoal={onVerifyGoal}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence mode="wait">
        {softSkillsEnabled && (softSkillsLoading ? (
          <motion.div
            key="soft-skills-skeleton"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="mt-6"
          >
            <SoftSkillsSkeleton />
          </motion.div>
        ) : (
          <motion.div
            key="soft-skills"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="mt-6"
          >
            <SoftSkillsSection
              softSkillsData={softSkillsData}
              onRatingChange={onSoftSkillRatingChange}
              canEditEmployeeRating={canEditEmployeeRating}
              canEditManagerRating={canEditManagerRating}
            />
          </motion.div>
        ))}
      </AnimatePresence>

      <AnimatePresence mode="wait">
        {softSkillsEnabled && (reviewSummaryLoading ? (
          <motion.div
            key="review-skeleton"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="mt-6"
          >
            <ReviewSummarySkeleton />
          </motion.div>
        ) : (
          <motion.div
            key="review-summary"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="mt-6"
          >
            {/* <ReviewSummary
              reviewSummary={reviewSummary}
              selectedMonth={selectedMonth}
              isExpanded={isReviewSummaryExpanded}
              onToggle={onToggleReviewSummary}
              onReviewChange={onReviewChange}
              isGoalsCycleEditable={isGoalsCycleEditable}
              canEditEmployeeResponse={canEditEmployeeResponse}
              canEditManagerResponse={canEditManagerResponse}
            /> */}
          </motion.div>
        ))}
      </AnimatePresence>

      {/* <Card
        className={`achievements-card rounded-[2.5rem] overflow-hidden shadow-sm mt-6 ${
          isDark ? '!bg-white/5 !border-white/10 ant-card-dark' : '!bg-white !border-slate-200'
        }`}
        title={
          <div className="flex items-center gap-2">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-amber-500">
              <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
            </svg>
            <h2 className={`text-[14px] font-black uppercase tracking-wider leading-none ${
              isDark ? '!text-white' : 'text-slate-900'
            }`}>
              ADDITIONAL ACHIEVEMENTS
            </h2>
            <Badge dot className="!w-3.5 !h-3.5" />
          </div>
        }
        extra={
          onAddAchievement && (
            <Button
              type="primary"
              icon={<Plus size={14} />}
              onClick={onAddAchievement}
              className={`!text-[10px] !font-black !uppercase !tracking-widest !h-auto !px-5 !py-2.5 !rounded-xl ${
                isDark ? '!bg-slate-900 hover:!bg-blue-600' : '!bg-slate-900 hover:!bg-blue-600'
              }`}
            >
              Add Achievement
            </Button>
          )
        }
      >
        {achievements.length > 0 ? (
          <div className="space-y-4">
            {achievements.map((achievement) => (
              <Card
                key={achievement.id}
                className={`rounded-2xl ${
                  isDark ? '!bg-white/5 !border-white/10' : '!bg-slate-50 !border-slate-100'
                }`}
                bodyStyle={{ padding: '16px' }}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h3 className={`text-sm font-black mb-1 ${
                      isDark ? '!text-white' : 'text-slate-900'
                    }`}>
                      {achievement.title}
                    </h3>
                    {achievement.description && (
                      <p className={`text-xs leading-relaxed ${
                        isDark ? 'text-white/80' : 'text-slate-600'
                      }`}>
                        {achievement.description}
                      </p>
                    )}
                  </div>
                  {achievement.date && (
                    <Tag className={`!text-[10px] !font-black !uppercase !tracking-widest ${
                      isDark ? '!text-white/60' : '!text-slate-400'
                    }`}>
                      {achievement.date}
                    </Tag>
                  )}
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <div className={`py-12 flex flex-col items-center justify-center border-2 border-dashed rounded-[2.5rem] ${
            isDark ? 'border-white/10' : 'border-slate-100'
          }`}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={isDark ? 'text-white/20' : 'text-slate-100'}>
              <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
            </svg>
            <p className={`text-sm font-black uppercase tracking-widest mt-4 ${
              isDark ? 'text-white/60' : 'text-slate-400'
            }`}>
              No additional achievements yet.
            </p>
            <p className={`text-[10px] font-black uppercase tracking-widest mt-1 ${
              isDark ? 'text-white/40' : 'text-slate-300'
            }`}>
              Click "Add Achievement" to record one.
            </p>
          </div>
        )}
      </Card> */}

      {/* Footer slot lives in MyGoals scroll container (id="goals-cycle-footer-slot")
          so position: sticky engages reliably against the page-level scroll context. */}
    </div>
  );
};

export default GoalsDetailView;
